import { useId, useState, type ReactNode } from 'react';
import { useT } from '../i18n/index.js';
import { Ico } from './Iconos.js';

/**
 * Las gráficas de «Análisis»: bars, columns and a grid of hours, drawn with the page itself.
 *
 * No chart library: the app works with no signal and loads on cheap phones, and these few shapes
 * are a handful of boxes each. Every one says what it shows in words too, for a screen reader and
 * for whoever wants the figure rather than the shape.
 */

/**
 * How much a figure moved against the period before: up green, down red, or the other way for a
 * cost. Nothing at all with nothing before it to move from: the page says so once, under the
 * figures, rather than every figure saying it.
 */
export function Cambio({ bp, alReves = false }: { bp: number | null; alReves?: boolean }) {
  if (bp === null) return null;
  const sube = bp > 0;
  const bueno = bp === 0 ? null : sube !== alReves;
  const color = bueno === null ? 'text-slate-400' : bueno ? 'text-emerald-400' : 'text-rose-400';
  return (
    <span className={`tabular text-xs font-medium ${color}`} data-cambio={bp}>
      {/* Past a thousand per cent a decimal says nothing more. */}
      {bp === 0 ? '=' : sube ? '▲' : '▼'} {Math.abs(bp / 100).toFixed(bp % 100 === 0 || Math.abs(bp) >= 100_000 ? 0 : 1)}%
    </span>
  );
}

/**
 * A figure in a box of its own: its name, the figure, red when it is a loss, and under it how it
 * moved. With `onAbrir` the box is a button, a chevron in its corner, that opens what it adds up
 * (the owner, 2026-10-13: «tap any figure to see the records behind it»).
 */
export function Cifra({
  nombre,
  valor,
  debajo,
  datos,
  negativo = false,
  onAbrir,
}: {
  nombre: string;
  valor: string;
  debajo?: ReactNode;
  datos?: string;
  negativo?: boolean;
  onAbrir?: () => void;
}) {
  const caja = 'min-w-0 rounded-2xl bg-linear-to-b from-slate-900 to-slate-900/50 p-3.5 ring-1 ring-white/5 ring-inset';
  const cuerpo = (
    <>
      <span className="block pr-4 text-xs text-slate-400">{nombre}</span>
      {/* «C$ 1,234,567» is wider than half a 320 px phone even at the smaller size there: it breaks rather than run out of its box. */}
      <span className={`tabular mt-1 block text-lg leading-tight font-semibold wrap-break-word max-[359px]:text-base ${negativo ? 'text-rose-300' : ''}`}>
        {valor}
      </span>
      {debajo && <span className="mt-1 flex flex-wrap items-baseline gap-x-1.5">{debajo}</span>}
    </>
  );
  if (!onAbrir) {
    return (
      <div className={caja} data-cifra={datos}>
        {cuerpo}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onAbrir}
      className={`${caja} relative block w-full text-left transition hover:from-slate-800 hover:to-slate-800/60 hover:ring-white/15`}
      data-cifra={datos}
    >
      <Ico icono="adelante" className="absolute top-3 right-3 size-4 text-slate-500" />
      {cuerpo}
    </button>
  );
}

export interface Barra {
  clave: string;
  etiqueta: ReactNode;
  valor: number;
  /** The figure as it is read, beside the name. */
  texto: string;
  detalle?: string;
  /** Its own colour, where the bars say different things: what is owed by how old it is. */
  tono?: string;
  /** Opens what the figure adds up: the whole row is the button, a chevron after the figure. Not with a link in `etiqueta`. */
  onAbrir?: () => void;
}

/**
 * Ranked bars: the name and the figure on a line, a bar under it as long as its share of the
 * largest. A negative figure has no bar, and its figure says so in red.
 */
export function Barras({ filas, tono = 'bg-marca-500', datos }: { filas: readonly Barra[]; tono?: string; datos?: string }) {
  const max = Math.max(0, ...filas.map((f) => f.valor));
  return (
    <ul className="flex flex-col gap-3" data-barras={datos}>
      {filas.map((f) => {
        const cuerpo = (
          <>
            <span className="flex items-baseline justify-between gap-3 text-sm">
              <span className="min-w-0 wrap-break-word">{f.etiqueta}</span>
              <span className={`tabular inline-flex shrink-0 items-center gap-1 ${f.valor < 0 ? 'text-rose-400' : ''}`}>
                {f.texto}
                {f.onAbrir && <Ico icono="adelante" className="size-3.5 text-slate-500" />}
              </span>
            </span>
            {f.detalle && <span className="block text-xs text-slate-500">{f.detalle}</span>}
            <span className="mt-1 block h-1.5 overflow-hidden rounded-full bg-slate-800">
              <span
                className={`block h-full rounded-full ${f.tono ?? tono}`}
                style={{ width: `${max > 0 && f.valor > 0 ? Math.max(2, (f.valor / max) * 100) : 0}%` }}
              />
            </span>
          </>
        );
        return (
          <li key={f.clave} data-barra={f.clave}>
            {f.onAbrir ? (
              <button type="button" data-sin-icono="fila" onClick={f.onAbrir} className="-mx-2 block w-[calc(100%+1rem)] rounded-lg px-2 py-1 text-left transition hover:bg-white/5">
                {cuerpo}
              </button>
            ) : (
              cuerpo
            )}
          </li>
        );
      })}
    </ul>
  );
}

export interface Columna {
  clave: string;
  /** What the axis says under it, on the first, the middle and the last. */
  etiqueta: string;
  /** The column's height: what was sold. */
  valor: number;
  /** The part of it filled brighter: what it left. */
  parte: number;
  /** All of it in words: what a tap on it shows, and what a screen reader says. */
  texto: string;
}

/**
 * Columns over time: what was sold each day, week or month, the part that was profit brighter. A
 * phone has no pointer to rest on a column, so a tap picks one and its figures show under them.
 */
export function Columnas({ columnas, resumen }: { columnas: readonly Columna[]; resumen: string }) {
  const t = useT();
  const [elegida, setElegida] = useState<string | null>(null);
  const max = Math.max(0, ...columnas.map((c) => c.valor));
  const vista = columnas.find((c) => c.clave === elegida);
  const primera = columnas[0];
  const media = columnas[Math.floor((columnas.length - 1) / 2)];
  const ultima = columnas.at(-1);
  return (
    <figure data-columnas aria-label={resumen}>
      {/* A floor under them, so a day that sold nothing reads as nothing rather than as a gap. */}
      <div className="flex h-36 items-end gap-px border-b border-slate-700 sm:gap-1">
        {columnas.map((c) => {
          const alto = max > 0 ? (c.valor / max) * 100 : 0;
          const parte = c.valor > 0 ? Math.min(100, Math.max(0, (c.parte / c.valor) * 100)) : 0;
          const esta = c.clave === elegida;
          return (
            <button
              key={c.clave}
              type="button"
              data-sin-icono="tecla"
              onClick={() => setElegida(esta ? null : c.clave)}
              aria-pressed={esta}
              aria-label={c.texto}
              title={c.texto}
              className="group flex h-full min-w-0 flex-1 items-end rounded-t-sm transition-colors hover:bg-white/5"
              data-columna={c.clave}
            >
              <span
                className={`flex w-full flex-col justify-end overflow-hidden rounded-t-sm ${esta ? 'bg-marca-300/50' : 'bg-marca-500/35 group-hover:bg-marca-400/50'}`}
                // A day that sold anything shows, however small beside the best.
                style={{ height: c.valor > 0 ? `max(2px, ${alto}%)` : '0' }}
              >
                <span className={`block w-full ${esta ? 'bg-marca-300' : 'bg-marca-400 group-hover:bg-marca-300'}`} style={{ height: `${parte}%` }} />
              </span>
            </button>
          );
        })}
      </div>
      {/*
        Three marks spread across, not one under each column: a month's thirty labels are a smudge
        at 320 px, and a label under a column eight pixels wide spills past the screen's edge.
      */}
      <div className="mt-1 flex justify-between gap-2 text-[10px] text-slate-500">
        {primera && <span>{primera.etiqueta}</span>}
        {media && media !== primera && media !== ultima && <span>{media.etiqueta}</span>}
        {ultima && ultima !== primera && <span>{ultima.etiqueta}</span>}
      </div>
      <figcaption className="mt-2 min-h-5 text-sm text-slate-300" aria-live="polite" data-columna-elegida>
        {vista ? vista.texto : <span className="text-xs text-slate-500">{t('analisis.toqueColumna')}</span>}
      </figcaption>
    </figure>
  );
}

/** A day of a running figure: what the money comes to that day. */
export interface PuntoDeLinea {
  clave: string;
  /** What the axis says under it, on the first, the middle and the last. */
  etiqueta: string;
  valor: number;
  /** All of it in words: what a tap on it shows, and what a screen reader says. */
  texto: string;
}

/**
 * A line over days: where a running figure stands each day, which can go under nothing, as the
 * money of the days ahead can, where a column has no way down. Zero is drawn when the line goes
 * under it, and what is under it is red. A tap on a day reads it out, as a column does.
 */
export function Linea({ puntos, resumen }: { puntos: readonly PuntoDeLinea[]; resumen: string }) {
  const t = useT();
  const id = useId();
  const [elegido, setElegido] = useState<string | null>(null);
  const min = Math.min(0, ...puntos.map((p) => p.valor));
  const max = Math.max(0, ...puntos.map((p) => p.valor));
  const alto = max - min || 1;
  const x = (i: number) => (puntos.length < 2 ? 50 : (i / (puntos.length - 1)) * 100);
  const y = (v: number) => ((max - v) / alto) * 100;
  const cero = y(0);
  const trazo = puntos.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(p.valor)}`).join(' ');
  const i = puntos.findIndex((p) => p.clave === elegido);
  const visto = i >= 0 ? puntos[i] : undefined;
  const primero = puntos[0];
  const medio = puntos[Math.floor((puntos.length - 1) / 2)];
  const ultimo = puntos.at(-1);
  return (
    <figure data-linea aria-label={resumen}>
      <div className="relative h-36 border-b border-slate-700">
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full overflow-visible" aria-hidden="true">
          <defs>
            <clipPath id={`${id}-arriba`}>
              <rect x="-1" y="-1" width="102" height={cero + 1} />
            </clipPath>
            <clipPath id={`${id}-abajo`}>
              <rect x="-1" y={cero} width="102" height={101 - cero} />
            </clipPath>
          </defs>
          {min < 0 && <line x1="0" x2="100" y1={cero} y2={cero} className="stroke-slate-500" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />}
          {visto && <line x1={x(i)} x2={x(i)} y1="0" y2="100" className="stroke-slate-500" vectorEffect="non-scaling-stroke" />}
          <path d={trazo} fill="none" className="stroke-marca-400" strokeWidth="2" vectorEffect="non-scaling-stroke" clipPath={`url(#${id}-arriba)`} />
          <path d={trazo} fill="none" className="stroke-rose-400" strokeWidth="2" vectorEffect="non-scaling-stroke" clipPath={`url(#${id}-abajo)`} />
        </svg>
        <div className="absolute inset-0 flex">
          {puntos.map((p) => (
            <button
              key={p.clave}
              type="button"
              data-sin-icono="tecla"
              onClick={() => setElegido(p.clave === elegido ? null : p.clave)}
              aria-pressed={p.clave === elegido}
              aria-label={p.texto}
              title={p.texto}
              className="h-full min-w-0 flex-1 transition-colors hover:bg-white/5"
              data-punto={p.clave}
            />
          ))}
        </div>
      </div>
      <div className="mt-1 flex justify-between gap-2 text-[10px] text-slate-500">
        {primero && <span>{primero.etiqueta}</span>}
        {medio && medio !== primero && medio !== ultimo && <span>{medio.etiqueta}</span>}
        {ultimo && ultimo !== primero && <span>{ultimo.etiqueta}</span>}
      </div>
      <figcaption className="mt-2 min-h-5 text-sm text-slate-300" aria-live="polite" data-punto-elegido>
        {visto ? visto.texto : <span className="text-xs text-slate-500">{t('analisis.toqueDia')}</span>}
      </figcaption>
    </figure>
  );
}

/** The hours always shown: a shop's day, 6 in the morning to 8 at night, wider if anything sold outside it. */
const ABRE = 6;
const CIERRA = 20;

/**
 * The week by hours: a row a day, Sunday first, a cell an hour, brighter the more it sold then.
 * The shop's whole day, so a week that sold at one hour alone is a row of small squares with one
 * lit, not seven squares as wide as the phone; and no wider than a hand on a big screen.
 */
export function MapaDeCalor({
  celdas,
  max,
  desde,
  hasta,
  dias,
  resumen,
}: {
  celdas: readonly (readonly number[])[];
  max: number;
  desde: number;
  hasta: number;
  /** The days' short names, Sunday first. */
  dias: readonly string[];
  resumen: string;
}) {
  const inicio = Math.min(desde, ABRE);
  const fin = Math.max(hasta, CIERRA);
  const horas = Array.from({ length: fin - inicio + 1 }, (_, i) => inicio + i);
  return (
    <figure role="img" aria-label={resumen} className="max-w-xl" data-mapa-de-calor>
      <div className="grid gap-0.5" style={{ gridTemplateColumns: `1.75rem repeat(${horas.length}, minmax(0, 1fr))` }}>
        <span />
        {horas.map((h) => (
          <span key={h} className="tabular text-center text-[10px] text-slate-500">
            {(h - inicio) % 3 === 0 ? h : ''}
          </span>
        ))}
        {dias.map((dia, d) => (
          <div key={d} className="contents">
            <span className="self-center truncate text-[10px] text-slate-500">{dia}</span>
            {horas.map((h) => {
              const n = celdas[d]?.[h] ?? 0;
              return (
                <span
                  key={h}
                  className={`aspect-square rounded-sm ${n === 0 ? 'bg-slate-800/70' : 'bg-marca-400'}`}
                  style={n === 0 ? undefined : { opacity: 0.25 + 0.75 * (n / Math.max(1, max)) }}
                  title={`${dia} ${h}:00 · ${n}`}
                  data-celda={`${d}-${h}`}
                  data-n={n}
                />
              );
            })}
          </div>
        ))}
      </div>
    </figure>
  );
}
